import { endOfDayIn, startOfDayIn, zoneForLocations, zoneOffsetMinutes } from './format';
import type {
  DeletedCheckStatus,
  DeletedContact,
  LocationAiProfile,
  TimeZoneSettings,
  TabId,
  TabLevel,
  OverviewFilterState,
  OverviewResponse,
  AppointmentFilterState,
  AppointmentJourneyResponse,
  ContactJourneyResponse,
  JourneyResponse,
  OpportunitiesResponse,
  OpportunityFilterState,
  OpportunitySummaryResponse,
  ContactResolutionStatus,
  ConnectionsResponse,
  AppointmentSummaryResponse,
  AppointmentsResponse,
  AuthStatus,
  CallFilterState,
  CallsResponse,
  CallerQaResponse,
  CallQaStatus,
  CallReview,
  MessageFilterState,
  MessageSummaryResponse,
  MessagesResponse,
  PendingReply,
  WarmTransferRules,
  WarmTransferSetup,
  LocationSummary,
  Note,
  SummaryResponse,
  SyncStatus,
  LogEvent,
  LogFilterState,
  LogSummary,
  SyncRun,
  RecordingQuery,
  RecordingsResponse,
  RecordingsSummary,
  SavedView,
  ViewPage,
  AiProviderId,
  AiSettingsResponse,
  AiSummaryRecord,
  LocationAiSummary,
  LeadAiSummary,
  RatingStatus,
  UsersResponse,
  DashboardUser,
  GhlAppStatus,
  AssistantConversation,
  AssistantOverview,
  Role,
  Area,
  Level,
} from './types';

export function logParams(filters: LogFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.level) params.set('level', filters.level);
  if (filters.categories.length) params.set('category', filters.categories.join(','));
  if (filters.locationId) params.set('locationId', filters.locationId);
  if (filters.runId) params.set('runId', filters.runId);
  if (filters.q.trim()) params.set('q', filters.q.trim());
  if (filters.hours > 0) params.set('since', String(Date.now() - filters.hours * 3600_000));
  return params;
}

/** Length buckets for the Files tab, in seconds: [min, max). */
const LENGTHS: Record<RecordingQuery['length'], [number | null, number | null]> = {
  all: [null, null],
  short: [null, 60],
  '1-5': [60, 300],
  '5-15': [300, 900],
  '15+': [900, null],
};

export function recordingParams(query: RecordingQuery): URLSearchParams {
  const params = new URLSearchParams({
    sort: query.sort,
    dir: query.dir,
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.locationId) params.set('locationIds', query.locationId);
  if (query.q.trim()) params.set('q', query.q.trim());
  const [min, max] = LENGTHS[query.length];
  if (min !== null) params.set('minSeconds', String(min));
  if (max !== null) params.set('maxSeconds', String(max));
  return params;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

/**
 * The date range as the server needs it: the first and last instant of the
 * chosen calendar days in the zone that applies to these sub-accounts, and
 * that zone's offset for grouping by day.
 */
function setRange(params: URLSearchParams, from: string, to: string, locationIds: string[]) {
  const zone = zoneForLocations(locationIds);
  const start = startOfDayIn(from, zone);
  const end = endOfDayIn(to, zone);
  params.set('from', new Date(start).toISOString());
  params.set('to', new Date(end).toISOString());
  // One offset for the whole range, taken at its middle; a clock change inside
  // the range can move an hour's worth of items into the neighbouring day.
  params.set('tzOffsetMinutes', String(zoneOffsetMinutes(zone, Math.round((start + end) / 2))));
  params.set('tz', zone);
}

/** Turns the UI filter state into the query string every report endpoint reads. */
export function filterParams(filters: CallFilterState): URLSearchParams {
  const params = new URLSearchParams();
  // Date inputs are local calendar days; widen them to the full local day.
  setRange(params, filters.from, filters.to, filters.locationIds);
  if (filters.locationIds.length) params.set('locationIds', filters.locationIds.join(','));
  if (filters.userIds.length) params.set('userIds', filters.userIds.join(','));
  if (filters.outcomes.length) params.set('outcomes', filters.outcomes.join(','));
  if (filters.direction) params.set('direction', filters.direction);
  if (filters.search) params.set('search', filters.search);
  if (filters.activeClientsOnly) params.set('activeClientsOnly', 'true');
  if (filters.tags.length) {
    params.set('tags', filters.tags.join(','));
    params.set('tagMatch', filters.tagMatch);
  }
  if (filters.warmTransfer) params.set('warmTransfer', filters.warmTransfer);
  if (filters.qa) params.set('qa', filters.qa);
  return params;
}

/** Message filters: the shared date/location shape plus channels, sender and wait. */
export function messageParams(filters: MessageFilterState): URLSearchParams {
  const params = new URLSearchParams();
  setRange(params, filters.from, filters.to, filters.locationIds);
  if (filters.locationIds.length) params.set('locationIds', filters.locationIds.join(','));
  if (filters.userIds.length) params.set('userIds', filters.userIds.join(','));
  if (filters.channels.length) params.set('channels', filters.channels.join(','));
  if (filters.direction) params.set('direction', filters.direction);
  if (filters.sender) params.set('sender', filters.sender);
  if (filters.search) params.set('search', filters.search);
  if (filters.activeClientsOnly) params.set('activeClientsOnly', 'true');
  if (filters.tags.length) {
    params.set('tags', filters.tags.join(','));
    params.set('tagMatch', filters.tagMatch);
  }
  if (filters.minWaitHours) params.set('minWaitHours', filters.minWaitHours);
  return params;
}

/** Appointment filters share the date/location shape, plus their own dimensions. */
export function appointmentParams(filters: AppointmentFilterState): URLSearchParams {
  const params = new URLSearchParams();
  setRange(params, filters.from, filters.to, filters.locationIds);
  params.set('dateBasis', filters.dateBasis);
  params.set('userBasis', filters.userBasis);
  if (filters.locationIds.length) params.set('locationIds', filters.locationIds.join(','));
  if (filters.userIds.length) params.set('userIds', filters.userIds.join(','));
  if (filters.calendarIds.length) params.set('calendarIds', filters.calendarIds.join(','));
  if (filters.outcomes.length) params.set('outcomes', filters.outcomes.join(','));
  if (filters.search) params.set('search', filters.search);
  if (filters.activeClientsOnly) params.set('activeClientsOnly', 'true');
  if (filters.tags.length) {
    params.set('tags', filters.tags.join(','));
    params.set('tagMatch', filters.tagMatch);
  }
  return params;
}

export function opportunityParams(filters: OpportunityFilterState): URLSearchParams {
  const params = new URLSearchParams();
  setRange(params, filters.from, filters.to, filters.locationIds);
  if (filters.locationIds.length) params.set('locationIds', filters.locationIds.join(','));
  if (filters.userIds.length) params.set('userIds', filters.userIds.join(','));
  if (filters.statuses.length) params.set('statuses', filters.statuses.join(','));
  if (filters.pipelineIds.length) params.set('pipelineIds', filters.pipelineIds.join(','));
  if (filters.stageIds.length) params.set('stageIds', filters.stageIds.join(','));
  if (filters.search) params.set('search', filters.search);
  if (filters.activeClientsOnly) params.set('activeClientsOnly', 'true');
  if (filters.tags.length) params.set('tags', filters.tags.join(','));
  if (filters.tags.length) params.set('tagMatch', filters.tagMatch);
  if (filters.includeDeleted) params.set('includeDeleted', 'true');
  if (filters.appointmentFrom) {
    params.set('appointmentFrom', new Date(startOfDayIn(filters.appointmentFrom, zoneForLocations(filters.locationIds))).toISOString());
  }
  if (filters.appointmentTo) {
    params.set('appointmentTo', new Date(endOfDayIn(filters.appointmentTo, zoneForLocations(filters.locationIds))).toISOString());
  }
  if (filters.appointmentState !== 'any') params.set('appointmentState', filters.appointmentState);
  if (filters.sort !== 'created') params.set('sort', filters.sort);
  if (filters.minLeadScore !== '') params.set('minLeadScore', filters.minLeadScore);
  return params;
}

export function overviewParams(filters: OverviewFilterState): URLSearchParams {
  const params = new URLSearchParams();
  setRange(params, filters.from, filters.to, filters.locationIds);
  if (filters.locationIds.length) params.set('locationIds', filters.locationIds.join(','));
  if (filters.activeClientsOnly) params.set('activeClientsOnly', 'true');
  return params;
}

export const api = {
  overview: (filters: OverviewFilterState) => request<OverviewResponse>(`/api/reports/overview?${overviewParams(filters)}`),
  authStatus: () => request<AuthStatus>('/api/auth/status'),
  login: (password: string, email?: string) =>
    request<{ ok: true }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ password, email: email || undefined }) }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  connections: () => request<ConnectionsResponse>('/api/connections'),
  connectLocation: (input: { locationId: string; token: string; label?: string }) =>
    request<{ locationId: string; name: string | null }>('/api/connections/location', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  connectAgency: (input: { companyId: string; token: string; label?: string }) =>
    request<{ companyId: string }>('/api/connections/agency', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deleteConnection: (id: string) =>
    request<{ ok: true }>(`/api/connections/${id}`, { method: 'DELETE' }),
  verifyConnections: () =>
    request<{ results: { id: string; ok: boolean; error?: string }[] }>('/api/connections/verify', {
      method: 'POST',
    }),

  locations: () => request<{ locations: LocationSummary[] }>('/api/locations'),
  createLocation: (input: Record<string, string>) =>
    request<{ id: string }>('/api/locations', { method: 'POST', body: JSON.stringify(input) }),
  setLocationFlags: (locationId: string, flags: { trackingEnabled?: boolean; isActiveClient?: boolean }) =>
    request<{ ok: true }>(`/api/locations/${locationId}`, {
      method: 'PATCH',
      body: JSON.stringify(flags),
    }),

  notes: (locationId: string) => request<{ notes: Note[] }>(`/api/locations/${locationId}/notes`),
  createNote: (locationId: string, input: { title?: string; body: string; author?: string; pinned?: boolean }) =>
    request<{ note: Note }>(`/api/locations/${locationId}/notes`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateNote: (locationId: string, noteId: string, input: Partial<Note>) =>
    request<{ note: Note }>(`/api/locations/${locationId}/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  deleteNote: (locationId: string, noteId: string) =>
    request<{ ok: true }>(`/api/locations/${locationId}/notes/${noteId}`, { method: 'DELETE' }),

  summary: (filters: CallFilterState) =>
    request<SummaryResponse>(`/api/reports/summary?${filterParams(filters)}`),
  callers: (filters: CallFilterState) =>
    request<{ callers: { userId: string | null; userName: string | null; locationId: string; total: number }[] }>(
      `/api/reports/callers?${filterParams(filters)}`,
    ),

  calls: (filters: CallFilterState, page: number, pageSize: number) => {
    const params = filterParams(filters);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return request<CallsResponse>(`/api/calls?${params}`);
  },
  callsCsvUrl: (filters: CallFilterState) => `/api/calls/export.csv?${filterParams(filters)}`,
  recordingUrl: (callId: string) => `/api/calls/${callId}/recording`,
  checkRecording: (callId: string) =>
    request<{ available: boolean; reason?: string; contentType?: string | null }>(
      `/api/calls/${callId}/recording/check`,
    ),
  rebucketCalls: () => request<{ examined: number; updated: number }>('/api/sync/rebucket', { method: 'POST' }),

  appointmentSummary: (filters: AppointmentFilterState) =>
    request<AppointmentSummaryResponse>(`/api/reports/appointments/summary?${appointmentParams(filters)}`),
  appointmentPeople: (filters: AppointmentFilterState) =>
    request<{ people: { userId: string | null; userName: string | null; locationId: string; total: number }[] }>(
      `/api/reports/appointments/people?${appointmentParams(filters)}`,
    ),
  appointmentCalendars: (filters: AppointmentFilterState) =>
    request<{ calendars: { id: string; name: string | null; locationId: string }[] }>(
      `/api/appointments/calendars?${appointmentParams(filters)}`,
    ),
  appointments: (filters: AppointmentFilterState, page: number, pageSize: number) => {
    const params = appointmentParams(filters);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return request<AppointmentsResponse>(`/api/appointments?${params}`);
  },
  appointmentJourney: (id: string) =>
    request<AppointmentJourneyResponse>(`/api/appointments/${id}/journey`),
  appointmentsCsvUrl: (filters: AppointmentFilterState) =>
    `/api/appointments/export.csv?${appointmentParams(filters)}`,

  tags: (filters: CallFilterState) =>
    request<{ tags: { tag: string; contacts: number }[] }>(`/api/reports/tags?${filterParams(filters)}`),

  opportunitySummary: (filters: OpportunityFilterState) =>
    request<OpportunitySummaryResponse>(`/api/reports/opportunities/summary?${opportunityParams(filters)}`),
  opportunityPeople: (filters: OpportunityFilterState) =>
    request<{ people: { userId: string | null; userName: string | null; locationId: string; total: number }[] }>(
      `/api/reports/opportunities/people?${opportunityParams(filters)}`,
    ),
  opportunityPipelines: (filters: OpportunityFilterState) =>
    request<{
      pipelines: { id: string; name: string | null; locationId: string }[];
      stages: { id: string; name: string | null; pipelineId: string; position: number }[];
    }>(`/api/opportunities/pipelines?${opportunityParams(filters)}`),
  opportunities: (filters: OpportunityFilterState, page: number, pageSize: number) => {
    const params = opportunityParams(filters);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return request<OpportunitiesResponse>(`/api/opportunities?${params}`);
  },
  opportunityJourney: (id: string) => request<JourneyResponse>(`/api/opportunities/${id}/journey`),
  contactJourney: (contactId: string) =>
    request<ContactJourneyResponse>(`/api/journeys/contacts/${contactId}`),
  activitySample: () =>
    request<{
      byType: { message_type: string; count: number; with_user: number }[];
      samples: { id: string; messageType: string | null; body: string | null; userId: string | null }[];
    }>('/api/journeys/activity/sample'),
  opportunitiesCsvUrl: (filters: OpportunityFilterState) =>
    `/api/opportunities/export.csv?${opportunityParams(filters)}`,

  syncStatus: () => request<SyncStatus>('/api/sync/status'),
  views: (page?: ViewPage) => request<{ views: SavedView[] }>(`/api/views${page ? `?page=${page}` : ''}`),
  createView: (body: { page: ViewPage; name: string; filters: Record<string, unknown>; isDefault?: boolean }) =>
    request<{ view: SavedView }>('/api/views', { method: 'POST', body: JSON.stringify(body) }),
  updateView: (id: string, body: { name?: string; filters?: Record<string, unknown>; isDefault?: boolean }) =>
    request<{ view: SavedView }>(`/api/views/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteView: (id: string) => request<{ ok: true }>(`/api/views/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  aiSettings: () => request<AiSettingsResponse>('/api/ai/settings'),
  users: () => request<UsersResponse>('/api/users'),
  createUser: (body: {
    email: string;
    name?: string;
    password: string;
    role: Role;
    permissions?: Partial<Record<Area, Level>>;
    tabs?: Partial<Record<TabId, TabLevel>>;
    locationIds?: string[] | null;
  }) => request<{ user: DashboardUser }>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (
    id: string,
    body: Partial<{
      email: string;
      name: string | null;
      password: string;
      role: Role;
      permissions: Partial<Record<Area, Level>>;
      tabs: Partial<Record<TabId, TabLevel>>;
      locationIds: string[] | null;
      disabled: boolean;
    }>,
  ) => request<{ user: DashboardUser }>(`/api/users/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteUser: (id: string) => request<{ ok: true }>(`/api/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  ghlApp: () => request<GhlAppStatus>('/api/ghl-app'),
  saveGhlApp: (body: { clientId?: string; clientSecret?: string; redirectUri?: string; scopes?: string[] }) =>
    request<GhlAppStatus>('/api/ghl-app', { method: 'PUT', body: JSON.stringify(body) }),
  assistant: () => request<AssistantOverview>('/api/assistant'),
  newConversation: (locationId: string | null) =>
    request<AssistantConversation>('/api/assistant/conversations', { method: 'POST', body: JSON.stringify({ locationId }) }),
  conversation: (id: string) => request<AssistantConversation>(`/api/assistant/conversations/${encodeURIComponent(id)}`),
  setConversationLocation: (id: string, locationId: string | null) =>
    request<AssistantConversation>(`/api/assistant/conversations/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ locationId }),
    }),
  deleteConversation: (id: string) =>
    request<{ ok: true }>(`/api/assistant/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  sendAssistantMessage: (id: string, text: string) =>
    request<AssistantConversation>(`/api/assistant/conversations/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  decideAction: (id: string, decision: 'approve' | 'reject') =>
    request<AssistantConversation>(`/api/assistant/actions/${encodeURIComponent(id)}/${decision}`, { method: 'POST' }),
  ratingStatus: () => request<RatingStatus>('/api/ai/ratings/status'),
  runRatings: () =>
    request<{ result: { rated?: number; failed?: number; error?: string | null; skipped?: string }; status: RatingStatus }>(
      '/api/ai/ratings/run',
      { method: 'POST' },
    ),
  resetRatings: (scope?: 'default') =>
    request<{ marked: number; status: RatingStatus }>(`/api/ai/ratings/reset${scope ? `?scope=${scope}` : ''}`, { method: 'POST' }),
  saveTimeZones: (body: Partial<TimeZoneSettings>) =>
    request<TimeZoneSettings>('/api/settings/time-zones', { method: 'PUT', body: JSON.stringify(body) }),
  deletedContacts: (locationId: string) =>
    request<{ status: DeletedCheckStatus | null; contacts: DeletedContact[] }>(
      `/api/locations/${encodeURIComponent(locationId)}/deleted-contacts`,
    ),
  checkDeletedContacts: (locationId: string) =>
    request<{ status: DeletedCheckStatus }>(`/api/locations/${encodeURIComponent(locationId)}/deleted-contacts/check`, {
      method: 'POST',
    }),
  deletedContactsCsvUrl: (locationId: string) => `/api/locations/${encodeURIComponent(locationId)}/deleted-contacts/export.csv`,
  messageSummary: (filters: MessageFilterState) =>
    request<MessageSummaryResponse>(`/api/messages/summary?${messageParams(filters)}`),
  pendingReplies: (filters: MessageFilterState, limit = 500) =>
    request<{ pending: PendingReply[]; total: number; wait: { key: string; label: string; count: number }[] }>(
      `/api/messages/pending?${messageParams(filters)}&limit=${limit}`,
    ),
  pendingRepliesCsvUrl: (filters: MessageFilterState) => `/api/messages/pending/export.csv?${messageParams(filters)}`,
  messages: (filters: MessageFilterState, page: number, pageSize: number) =>
    request<MessagesResponse>(`/api/messages?${messageParams(filters)}&page=${page}&pageSize=${pageSize}`),
  messagesCsvUrl: (filters: MessageFilterState) => `/api/messages/export.csv?${messageParams(filters)}`,
  messageSenders: (filters: MessageFilterState) =>
    request<{ senders: { userId: string | null; userName: string | null; locationId: string; total: number }[] }>(
      `/api/messages/senders?${messageParams(filters)}`,
    ),
  callerQa: (filters: CallFilterState) => request<CallerQaResponse>(`/api/reports/caller-qa?${filterParams(filters)}`),
  callReview: (callId: string) =>
    request<{ review: CallReview | null; minSeconds: number }>(`/api/calls/${encodeURIComponent(callId)}/review`),
  reviewCall: (callId: string) =>
    request<{ review: CallReview }>(`/api/ai/call-qa/call/${encodeURIComponent(callId)}`, { method: 'POST' }),
  callQaStatus: () => request<CallQaStatus>('/api/ai/call-qa/status'),
  runCallQa: () =>
    request<{
      result: { reviewed?: number; failed?: number; noTranscript?: number; error?: string | null; skipped?: string };
      status: CallQaStatus;
    }>('/api/ai/call-qa/run', { method: 'POST' }),
  resetCallQa: (scope?: 'default') =>
    request<{ cleared: number; status: CallQaStatus }>(`/api/ai/call-qa/reset${scope ? `?scope=${scope}` : ''}`, { method: 'POST' }),
  warmTransfers: (locationId: string) =>
    request<WarmTransferSetup>(`/api/locations/${encodeURIComponent(locationId)}/warm-transfers`),
  saveWarmTransfers: (locationId: string, rules: WarmTransferRules) =>
    request<WarmTransferSetup>(`/api/locations/${encodeURIComponent(locationId)}/warm-transfers`, {
      method: 'PUT',
      body: JSON.stringify(rules),
    }),
  aiProfile: (locationId: string) => request<LocationAiProfile>(`/api/locations/${encodeURIComponent(locationId)}/ai-profile`),
  saveAiProfile: (locationId: string, body: { businessContext?: string; ratingPrompt?: string; callQaPrompt?: string }) =>
    request<LocationAiProfile>(`/api/locations/${encodeURIComponent(locationId)}/ai-profile`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  rerateLocation: (locationId: string) =>
    request<{ marked: number }>(`/api/locations/${encodeURIComponent(locationId)}/ai-profile/rerate`, { method: 'POST' }),
  rateContact: (contactId: string) =>
    request<{ rating: { score: number; reason: string | null; temperature: string | null; confidence: string | null; ratedAt: number } | null }>(
      `/api/ai/ratings/contact/${encodeURIComponent(contactId)}`,
      { method: 'POST' },
    ),
  saveAiSettings: (
    body: Partial<Pick<AiSettingsResponse, 'activeProvider' | 'businessContext' | 'include'>> & {
      rating?: Partial<AiSettingsResponse['rating']>;
      callQa?: Partial<AiSettingsResponse['callQa']>;
    },
  ) =>
    request<AiSettingsResponse>('/api/ai/settings', { method: 'PUT', body: JSON.stringify(body) }),
  saveAiProvider: (provider: AiProviderId, body: { apiKey?: string; model?: string }) =>
    request<AiSettingsResponse>(`/api/ai/providers/${provider}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteAiProvider: (provider: AiProviderId) =>
    request<AiSettingsResponse>(`/api/ai/providers/${provider}`, { method: 'DELETE' }),
  aiModels: (provider: AiProviderId) => request<{ models: string[] }>(`/api/ai/providers/${provider}/models`),
  testAiProvider: (provider: AiProviderId) =>
    request<{ ok: boolean; model?: string; durationMs?: number; error?: string }>(`/api/ai/providers/${provider}/test`, {
      method: 'POST',
    }),
  locationSummary: (locationId: string, from: string, to: string) =>
    request<{ summary: AiSummaryRecord<LocationAiSummary> | null; ready: boolean }>(
      `/api/ai/summaries/location/${encodeURIComponent(locationId)}?from=${from}&to=${to}`,
    ),
  generateLocationSummary: (locationId: string, from: string, to: string) =>
    request<{ summary: AiSummaryRecord<LocationAiSummary> }>(
      `/api/ai/summaries/location/${encodeURIComponent(locationId)}?from=${from}&to=${to}`,
      { method: 'POST' },
    ),
  leadSummary: (contactId: string) =>
    request<{ summary: AiSummaryRecord<LeadAiSummary> | null; ready: boolean; newActivity: boolean }>(
      `/api/ai/summaries/contact/${encodeURIComponent(contactId)}`,
    ),
  generateLeadSummary: (contactId: string) =>
    request<{ summary: AiSummaryRecord<LeadAiSummary>; newActivity: boolean }>(
      `/api/ai/summaries/contact/${encodeURIComponent(contactId)}`,
      { method: 'POST' },
    ),
  logEvents: (filters: LogFilterState, before?: number) => {
    const params = logParams(filters);
    params.set('limit', '200');
    if (before) params.set('before', String(before));
    return request<{ events: LogEvent[]; hasMore: boolean }>(`/api/logs/events?${params}`);
  },
  logSummary: () => request<LogSummary>('/api/logs/summary'),
  syncRuns: (limit = 30) => request<{ runs: SyncRun[] }>(`/api/logs/runs?limit=${limit}`),
  logExportUrl: (filters: LogFilterState) => `/api/logs/export.csv?${logParams(filters)}`,
  recordings: (query: RecordingQuery) =>
    request<RecordingsResponse>(`/api/recordings?${recordingParams(query)}`),
  recordingsSummary: () => request<RecordingsSummary>('/api/recordings/summary'),
  deleteRecordings: (body: { callIds: string[] } | { shorterThanSeconds: number }) =>
    request<{ deleted: number; bytes: number; reclaimedNow: boolean }>('/api/recordings/delete', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  compactRecordings: () =>
    request<{ started: boolean; reason?: string }>('/api/recordings/compact', { method: 'POST' }),
  contactStatus: () => request<ContactResolutionStatus>('/api/sync/contacts/status'),
  resolveContacts: () => request<{ resolved: number }>('/api/sync/contacts/resolve', { method: 'POST' }),
  retryMissingContacts: () =>
    request<{ requeued: number }>('/api/sync/contacts/retry', { method: 'POST', body: JSON.stringify({}) }),
  runSync: (locationIds?: string[]) =>
    request<{ started: boolean; running: boolean }>('/api/sync/run', {
      method: 'POST',
      body: JSON.stringify(locationIds ? { locationIds } : {}),
    }),
};
